/**
 * BijrijderDialog - dialog voor het zetten/wijzigen van een bijrijder op een Routes__c.
 *
 * - Checkbox "Bijrijder nodig" — staat aan zonder dat er per se al een persoon gekozen is.
 * - Select met alle actieve medewerkers (`staff`) voor wie als bijrijder meerijdt.
 *   De lijst komt van GET /api/routes/chauffeurs en filtert bewust niet op een
 *   Salesforce-koppeling: sinds de cut heeft een nieuwe collega die niet meer.
 * - Optie "Nog niet bekend" laat alleen de checkbox aan staan.
 * - Wist de bijrijder als de checkbox uitgezet wordt en er geen wijziging plaatsvindt.
 */

import { useEffect, useState } from 'react';
import type { ChauffeurDto, RouteDetail } from '../../lib/salesforce/types';

interface BijrijderDialogProps {
  route: RouteDetail;
  chauffeurs: ChauffeurDto[];
  isOpen: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (input: { bijrijderId: string | null; bijrijderNodig: boolean }) => void;
}

export function BijrijderDialog({
  route,
  chauffeurs,
  isOpen,
  isSaving,
  onClose,
  onSave,
}: BijrijderDialogProps) {
  const [nodig, setNodig] = useState<boolean>(route.bijrijderNodig);
  const [bijrijderId, setBijrijderId] = useState<string>(route.bijrijderId ?? '');

  useEffect(() => {
    if (!isOpen) return;
    setNodig(route.bijrijderNodig);
    setBijrijderId(route.bijrijderId ?? '');
  }, [isOpen, route.bijrijderNodig, route.bijrijderId]);

  if (!isOpen) return null;

  const handleSave = () => {
    const finalBijrijderId = nodig && bijrijderId ? bijrijderId : null;
    const finalNodig = nodig || Boolean(finalBijrijderId);
    onSave({ bijrijderId: finalBijrijderId, bijrijderNodig: finalNodig });
  };

  const activeChauffeurs = chauffeurs.filter((c) => c.isActive);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Bijrijder</h3>
        <p className="text-sm text-gray-600 mb-4">
          Markeer dat deze route een extra persoon nodig heeft en koppel optioneel een
          medewerker. De bijrijder verschijnt op de kalender, in de route-header en in het rooster.
        </p>

        <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 mb-4">
          <input
            type="checkbox"
            checked={nodig}
            onChange={(e) => setNodig(e.target.checked)}
            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
          />
          <div>
            <div className="font-medium text-gray-900">Bijrijder nodig</div>
            <div className="text-xs text-gray-500">
              Zet aan zodra je weet dat er twee personen mee moeten — ook als de persoon nog niet bekend is.
            </div>
          </div>
        </label>

        <div className="mb-5">
          <label htmlFor="bijrijder-select" className="block text-sm font-medium text-gray-700 mb-2">
            Welke medewerker rijdt mee?
          </label>
          <select
            id="bijrijder-select"
            value={bijrijderId}
            onChange={(e) => setBijrijderId(e.target.value)}
            disabled={!nodig || isSaving}
            className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 disabled:bg-gray-50 disabled:text-gray-500"
          >
            <option value="">Nog niet bekend</option>
            {activeChauffeurs.map((c) => (
              <option key={c.id} value={c.id} disabled={c.id === route.chauffeurId}>
                {c.name}
                {c.id === route.chauffeurId ? ' (chauffeur van deze route)' : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">
            De chauffeur van deze route kan niet ook bijrijder zijn.
          </p>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Opslaan…
              </>
            ) : (
              'Opslaan'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
